import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export type LayoutEntityType = 'zone' | 'entry';

export type LayoutSelection = {
  type: LayoutEntityType;
  id: string;
} | null;

export type UseLayoutSelectionResult = {
  selection: LayoutSelection;
  select: (type: LayoutEntityType, id: string) => void;
  clear: () => void;
};

const SEARCH_PARAMS: Record<LayoutEntityType, string> = {
  zone: 'zone',
  entry: 'entry',
};

/**
 * The selected zone or entry, kept in the URL (`?zone=<id>` or `?entry=<id>`)
 * so a selection survives reloads and the back button walks through it.
 */
export const useLayoutSelection = (): UseLayoutSelectionResult => {
  const history = useHistory();
  const location = useLocation();

  const selection = React.useMemo((): LayoutSelection => {
    const searchParams = new URLSearchParams(location.search);
    const entryId = searchParams.get(SEARCH_PARAMS.entry);
    if (entryId) return { type: 'entry', id: entryId };
    const zoneId = searchParams.get(SEARCH_PARAMS.zone);
    if (zoneId) return { type: 'zone', id: zoneId };
    return null;
  }, [location.search]);

  const navigate = React.useCallback(
    (next: LayoutSelection): void => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete(SEARCH_PARAMS.zone);
      searchParams.delete(SEARCH_PARAMS.entry);
      if (next) searchParams.set(SEARCH_PARAMS[next.type], next.id);
      const nextLocation = {
        pathname: location.pathname,
        search: searchParams.toString(),
      };
      if (next) history.push(nextLocation);
      else history.replace(nextLocation);
    },
    [history, location.pathname, location.search],
  );

  const select = React.useCallback(
    (type: LayoutEntityType, id: string) => navigate({ type, id }),
    [navigate],
  );
  const clear = React.useCallback(() => navigate(null), [navigate]);

  return { selection, select, clear };
};
